import React, { useState, useEffect } from "react";
import "./CompanyDashboard.css";
import {
  ChevronDown,
  ChevronUp,
  Building,
  Folder
} from "lucide-react";
import { toast } from "react-hot-toast";
import Loader from "../Loader/Loader";
import CountUp from "react-countup";
import BatchDetailDialog from "./BatchDetailDialog";
import Datepicker from "../Datepicker/Datepicker";
import AdminDashboardService from "../../services/AdminDashboard.service";

const CallTracker = () => {
  const [companies, setCompanies] = useState([]);
  const [expandedCompanyId, setExpandedCompanyId] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [dateError, setDateError] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const[kpiStats, setKpiStats] = useState(null);

  const fetchDetail = async () => {
    try {
      setLoading(true);
       const payload = {
         start_date: startDate,
         end_date: endDate,
       }
      const res = await AdminDashboardService.getCallTrackerData(payload);
      if (res.status === 200 && res.data) {
        setCompanies(res.data.data || []);
        setKpiStats(res.data.stats || null);
      } else {
        toast.error("Failed to fetch company list");
      }
    } catch (error) {
      console.error("Error fetching companies:", error);
      toast.error("Error loading data");
    }
    finally{
      setLoading(false)
    }
  };

  useEffect(() => {
  if (!dateError) {
    fetchDetail();
  }
}, [startDate, endDate]);

  const handleStartDateChange = (date) => {
    setStartDate(date);
    if (endDate && date > endDate) {
      setDateError("Start date cannot be after end date");
    } else {
      setDateError("");
    }
  };

  const handleEndDateChange = (date) => {
    setEndDate(date);
    if (startDate && date < startDate) {
      setDateError("End date cannot be before start date");
    } else {
      setDateError("");
    }
  };

  const toggleCompany = (id) => {
    setExpandedCompanyId(expandedCompanyId === id ? null : id);
  };

  const openBatchDialog = (campaign) => {
    setSelectedCampaign(campaign);
    setIsDialogOpen(true);
  };

  const closeBatchDialog = () => {
    setIsDialogOpen(false);
    setSelectedCampaign(null);
  };

  return (
    <div className="dashboard-container">
      

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "0 12px",
          marginBottom: "10px",
          flexWrap: "wrap",
          gap: "12px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "8px",flexWrap: "wrap", gap: "12px" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
             
            <Datepicker
              name="startDate"
              value={startDate}
              onChange={handleStartDateChange}
              dateFormat="dd-MM-yyyy"
              placeholderText="Select Start Date"
              errorMessage={dateError}
              errorClass={dateError ? true : false}
            />
          </div>
          <span style={{ fontSize: "0.875rem", color: "#666" }}>to</span>
          
          <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
             <Datepicker
              name="endDate"
              value={endDate}
              onChange={handleEndDateChange}
              dateFormat="dd-MM-yyyy"
              placeholderText="Select End Date"
              errorMessage={dateError}
              errorClass={dateError ? true : false}
            />
          </div>
        </div>
      </div>
      {loading && <Loader />}
      <div className="kpi-strip" style={{ display: "flex", justifyContent: "space-around", marginBottom: "20px" }}>
        {kpiStats && kpiStats.map((kpi, i) => (
          <div className="kpi" key={i}>
            <p className="kpi-label">{kpi.label}</p>
            <div className="kpi-row">
              <span className="kpi-value">
                <CountUp
                  end={kpi.value}
                  duration={2}
                  formattingFn={(num) => {
                    if (num >= 1000) return (num / 1000).toFixed(1) + "k";
                    return num;
                  }}
                />
              </span>
              <span
                className={`kpi-delta ${
                  kpi.positive ? "pos" : "neg"
                }`}
              >
                {kpi.delta}
              </span>
            </div>
          </div>
        ))}
      </div>

      {!loading && companies.length === 0 && (
        <p style={{ textAlign: "center", marginTop: "20px" }}>
          No companies found.
        </p>
      )}

      {!loading &&
        companies.map((company) => {
          const isCompanyExpanded = expandedCompanyId === company.id;

          return (
            <div key={company.id} className="dashboardContainer">
              <header
                className="companyHeader"
                onClick={() => toggleCompany(company.id)}
                style={{ cursor: "pointer" }}
              >
                <div className="headerLeft">
                  <div className="companyIconWrapper">
                    <Building size={24} color="#3B82F6" />
                  </div>
                  <div className="headerInfo">
                    <h1>{company.company_name}</h1>
                    {company.company_description && (
                      <p>{company.company_description}</p>
                    )}
                  </div>
                </div>

                <div className="headerRight">
                  <span className="badge-1">
                    {company.campaigns?.length || 0} campaigns
                  </span>

                  <button className="iconButton">
                    {isCompanyExpanded ? (
                      <ChevronUp size={20} />
                    ) : (
                      <ChevronDown size={20} />
                    )}
                  </button>
                </div>
              </header>

              {/* Campaigns */}
              {isCompanyExpanded && (
                <section className="campaignsSection">
                  <div className="campaigns-header">
                    <span>Campaigns</span>
                    <div className="campaigns-stats">
                      <span>Allocation</span>
                      <span>Calls</span>
                      <span>Connected</span>
                      <span>Positive</span>
                      <span>Collected</span>
                      <span>Status</span>
                    </div>
                  </div>

                  {company.campaigns?.map((campaign) => {
                    return (
                      <div key={campaign.campaign_id} className="campaignItem">
                        <div
                          className="dashboardHeader"
                          onClick={(e) => {
                            e.stopPropagation();
                            openBatchDialog(campaign);
                          }}
                          style={{ cursor: "pointer" }}
                        >
                          <div className="headerLeft" >
                            <div className="campaignIconWrapper">
                              <Folder size={24} color="#9333EA" />
                            </div>
                            <div className="headerInfo" >
                              <div className="campaignTitle">
                                {campaign.campaign_name}
                              </div>
                              <p className="campaign-start-info">
                                Started {campaign.start_date} • {campaign.batches} batches
                              </p>
                              <div className="campaign-progress-bar">
                                <div
                                  className="campaign-progress-fill"
                                  style={{ width: `${campaign.connect_percentage}%` }}
                                ></div>
                              </div>
                              <span className="campaign-progress-text">
                                {campaign.connect_percentage}% Connected
                              </span>
                            </div>
                          </div>

                          <div className="headerRight">
                            <div className="campaign-summary-stats">
                              <div className="stat-item-summary">
                                <span className="stat-value-summary">
                                  {(campaign.allocation / 1000).toFixed(1)}k
                                </span>
                                <span className="stat-label-summary">allocated</span>
                              </div>
                              <div className="stat-item-summary">
                                <span className="stat-value-summary">
                                  {(campaign.attempted_calls / 1000).toFixed(1)}k
                                </span>
                                <span className="stat-label-summary">calls</span>
                              </div>
                              <div className="stat-item-summary">
                                <span className="stat-value-summary">
                                  {(campaign.connected_calls / 1000).toFixed(1)}k
                                </span>
                                <span className="stat-label-summary">connected</span>
                              </div>
                              <div className="stat-item-summary">
                                <span className="stat-value-summary">
                                  {(campaign.positive_calls / 1000).toFixed(1)}k
                                </span>
                                <span className="stat-label-summary">positive</span>
                              </div>
                              <div className="stat-item-summary">
                                <span className="stat-value-summary">
                                  {(campaign.collected / 1000).toFixed(1)}k
                                </span>
                                <span className="stat-label-summary">collected</span>
                              </div>
                            </div>

                            <div className="status-section">
                              <span
                                className={`statusBadge ${campaign.campaign_status}`}
                              >
                                {campaign.campaign_status === 'running' ? 'Active' : campaign.campaign_status}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </section>
              )}
            </div>
          );
        })}
      <BatchDetailDialog 
        isOpen={isDialogOpen}
        onClose={closeBatchDialog}
        campaign={selectedCampaign}
        start_date={selectedCampaign?.start_date}
        end_date={selectedCampaign?.end_date}
      />
    </div>
  );
};

export default CallTracker;